import type { 
  SavedName,
  SavedPlot,
  SavedTitle,
  SavedCharacter,
  SavedRelationship,
  SavedWikipedia,
  SavedImagePrompt,
  SavedEnemy,
  SavedItem,
  SavedWorldBuilding,
  SavedSkill,
  SavedCommunity,
  SavedDungeon,
  SavedSociety,
  SavedScene,
  SavedDocument
} from '@creanizer/types'
import { VersionManager } from './VersionManager'

export interface BulkExportData {
  version: string
  exportDate: string
  dataTypes: string[]
  statistics: {
    totalItems: number
    byType: Record<string, number>
  }
  data: {
    names?: SavedName[]
    plots?: SavedPlot[]
    titles?: SavedTitle[]
    characters?: SavedCharacter[]
    relationships?: SavedRelationship[]
    wikipedias?: SavedWikipedia[]
    imagePrompts?: SavedImagePrompt[]
    enemies?: SavedEnemy[]
    items?: SavedItem[]
    worlds?: SavedWorldBuilding[]
    skills?: SavedSkill[]
    communities?: SavedCommunity[]
    dungeons?: SavedDungeon[]
    societies?: SavedSociety[]
    scenes?: SavedScene[]
    documents?: SavedDocument[]
  }
}

export interface DataStatistics {
  names: number
  plots: number
  titles: number
  characters: number
  relationships: number
  wikipedias: number
  imagePrompts: number
  enemies: number
  items: number
  worlds: number
  skills: number
  communities: number
  dungeons: number
  societies: number
  scenes: number
  documents: number
  total: number
}

export class DataManager {
  private static instance: DataManager
  
  private constructor() {}
  
  static getInstance(): DataManager {
    if (!DataManager.instance) {
      DataManager.instance = new DataManager()
    }
    return DataManager.instance
  }
  
  // 全データを一括エクスポート（JSON）
  exportAllAsJSON(stores: {
    names?: SavedName[]
    plots?: SavedPlot[]
    titles?: SavedTitle[]
    characters?: SavedCharacter[]
    relationships?: SavedRelationship[]
    wikipedias?: SavedWikipedia[]
    imagePrompts?: SavedImagePrompt[]
    enemies?: SavedEnemy[]
    items?: SavedItem[]
    worlds?: SavedWorldBuilding[]
    skills?: SavedSkill[]
    communities?: SavedCommunity[]
    dungeons?: SavedDungeon[]
    societies?: SavedSociety[]
    scenes?: SavedScene[]
    documents?: SavedDocument[]
  }): string {
    const dataTypes: string[] = []
    const statistics: Record<string, number> = {}
    let totalItems = 0
    
    // データタイプと統計情報を収集
    if (stores.names && stores.names.length > 0) {
      dataTypes.push('names')
      statistics.names = stores.names.length
      totalItems += stores.names.length
    }
    if (stores.plots && stores.plots.length > 0) {
      dataTypes.push('plots')
      statistics.plots = stores.plots.length
      totalItems += stores.plots.length
    }
    if (stores.titles && stores.titles.length > 0) {
      dataTypes.push('titles')
      statistics.titles = stores.titles.length
      totalItems += stores.titles.length
    }
    if (stores.characters && stores.characters.length > 0) {
      dataTypes.push('characters')
      statistics.characters = stores.characters.length
      totalItems += stores.characters.length
    }
    if (stores.relationships && stores.relationships.length > 0) {
      dataTypes.push('relationships')
      statistics.relationships = stores.relationships.length
      totalItems += stores.relationships.length
    }
    if (stores.wikipedias && stores.wikipedias.length > 0) {
      dataTypes.push('wikipedias')
      statistics.wikipedias = stores.wikipedias.length
      totalItems += stores.wikipedias.length
    }
    if (stores.imagePrompts && stores.imagePrompts.length > 0) {
      dataTypes.push('imagePrompts')
      statistics.imagePrompts = stores.imagePrompts.length
      totalItems += stores.imagePrompts.length
    }
    if (stores.enemies && stores.enemies.length > 0) {
      dataTypes.push('enemies')
      statistics.enemies = stores.enemies.length
      totalItems += stores.enemies.length
    }
    if (stores.items && stores.items.length > 0) {
      dataTypes.push('items')
      statistics.items = stores.items.length
      totalItems += stores.items.length
    }
    if (stores.worlds && stores.worlds.length > 0) {
      dataTypes.push('worlds')
      statistics.worlds = stores.worlds.length
      totalItems += stores.worlds.length
    }
    if (stores.skills && stores.skills.length > 0) {
      dataTypes.push('skills')
      statistics.skills = stores.skills.length
      totalItems += stores.skills.length
    }
    if (stores.communities && stores.communities.length > 0) {
      dataTypes.push('communities')
      statistics.communities = stores.communities.length
      totalItems += stores.communities.length
    }
    if (stores.dungeons && stores.dungeons.length > 0) {
      dataTypes.push('dungeons')
      statistics.dungeons = stores.dungeons.length
      totalItems += stores.dungeons.length
    }
    if (stores.societies && stores.societies.length > 0) {
      dataTypes.push('societies')
      statistics.societies = stores.societies.length
      totalItems += stores.societies.length
    }
    if (stores.scenes && stores.scenes.length > 0) {
      dataTypes.push('scenes')
      statistics.scenes = stores.scenes.length
      totalItems += stores.scenes.length
    }
    if (stores.documents && stores.documents.length > 0) {
      dataTypes.push('documents')
      statistics.documents = stores.documents.length
      totalItems += stores.documents.length
    }
    
    const exportData: BulkExportData = {
      version: VersionManager.getCurrentVersion(),
      exportDate: new Date().toISOString(),
      dataTypes,
      statistics: {
        totalItems,
        byType: statistics
      },
      data: stores
    }
    
    return JSON.stringify(exportData, null, 2)
  }
  
  // 全データを一括エクスポート（テキスト）
  exportAllAsText(stores: {
    names?: SavedName[]
    plots?: SavedPlot[]
    titles?: SavedTitle[]
    characters?: SavedCharacter[]
    relationships?: SavedRelationship[]
    wikipedias?: SavedWikipedia[]
    imagePrompts?: SavedImagePrompt[]
    enemies?: SavedEnemy[]
    items?: SavedItem[]
    worlds?: SavedWorldBuilding[]
    skills?: SavedSkill[]
    communities?: SavedCommunity[]
    dungeons?: SavedDungeon[]
    societies?: SavedSociety[]
    scenes?: SavedScene[]
    documents?: SavedDocument[]
  }): string {
    const date = new Date().toLocaleString('ja-JP')
    let content = `【CreaNizer 一括データエクスポート】\n`
    content += `生成日時: ${date}\n`
    content += '=' .repeat(60) + '\n\n'
    
    // 統計情報
    const stats = this.getStatistics(stores)
    content += `【データ統計】\n`
    content += `総データ数: ${stats.total}件\n`
    if (stats.names > 0) content += `  名前: ${stats.names}件\n`
    if (stats.plots > 0) content += `  プロット: ${stats.plots}件\n`
    if (stats.titles > 0) content += `  タイトル: ${stats.titles}件\n`
    if (stats.characters > 0) content += `  キャラクター: ${stats.characters}件\n`
    if (stats.relationships > 0) content += `  関係性: ${stats.relationships}件\n`
    if (stats.wikipedias > 0) content += `  架空事典: ${stats.wikipedias}件\n`
    if (stats.imagePrompts > 0) content += `  画像プロンプト: ${stats.imagePrompts}件\n`
    if (stats.enemies > 0) content += `  エネミー: ${stats.enemies}件\n`
    if (stats.items > 0) content += `  アイテム: ${stats.items}件\n`
    if (stats.worlds > 0) content += `  世界観: ${stats.worlds}件\n`
    if (stats.skills > 0) content += `  スキル: ${stats.skills}件\n`
    if (stats.communities > 0) content += `  コミュニティ: ${stats.communities}件\n`
    if (stats.dungeons > 0) content += `  ダンジョン: ${stats.dungeons}件\n`
    if (stats.societies > 0) content += `  組織: ${stats.societies}件\n`
    if (stats.scenes > 0) content += `  シーン描写: ${stats.scenes}件\n`
    if (stats.documents > 0) content += `  文献: ${stats.documents}件\n`
    content += '\n' + '=' .repeat(60) + '\n\n'
    
    // 各データタイプの詳細
    if (stores.names && stores.names.length > 0) {
      content += this.formatNamesAsText(stores.names)
      content += '\n'
    }
    
    if (stores.plots && stores.plots.length > 0) {
      content += this.formatPlotsAsText(stores.plots)
      content += '\n'
    }
    
    if (stores.titles && stores.titles.length > 0) {
      content += this.formatTitlesAsText(stores.titles)
      content += '\n'
    }
    
    if (stores.characters && stores.characters.length > 0) {
      content += this.formatCharactersAsText(stores.characters)
      content += '\n'
    }
    
    if (stores.relationships && stores.relationships.length > 0) {
      content += this.formatRelationshipsAsText(stores.relationships)
      content += '\n'
    }
    
    if (stores.wikipedias && stores.wikipedias.length > 0) {
      content += this.formatWikipediasAsText(stores.wikipedias)
      content += '\n'
    }
    
    if (stores.imagePrompts && stores.imagePrompts.length > 0) {
      content += this.formatImagePromptsAsText(stores.imagePrompts)
      content += '\n'
    }
    
    if (stores.enemies && stores.enemies.length > 0) {
      content += this.formatEnemiesAsText(stores.enemies)
      content += '\n'
    }
    
    if (stores.items && stores.items.length > 0) {
      content += this.formatItemsAsText(stores.items)
      content += '\n'
    }
    
    if (stores.worlds && stores.worlds.length > 0) {
      content += this.formatWorldsAsText(stores.worlds)
      content += '\n'
    }
    
    if (stores.skills && stores.skills.length > 0) {
      content += this.formatSkillsAsText(stores.skills)
    }
    
    if (stores.communities && stores.communities.length > 0) {
      content += this.formatCommunitiesAsText(stores.communities)
    }
    
    if (stores.dungeons && stores.dungeons.length > 0) {
      content += this.formatDungeonsAsText(stores.dungeons)
    }
    
    if (stores.societies && stores.societies.length > 0) {
      content += this.formatSocietiesAsText(stores.societies)
    }
    
    if (stores.scenes && stores.scenes.length > 0) {
      content += this.formatScenesAsText(stores.scenes)
    }
    
    if (stores.documents && stores.documents.length > 0) {
      content += this.formatDocumentsAsText(stores.documents)
    }
    
    return content
  }
  
  // インポートデータの検証とパース
  validateAndParseImport(jsonString: string): BulkExportData {
    let data: any
    
    try {
      data = JSON.parse(jsonString)
    } catch (e) {
      throw new Error('JSONファイルの解析に失敗しました。ファイル形式を確認してください。')
    }
    
    // データ構造の検証（validateDataStructureが必要に応じてデータを変換）
    if (!VersionManager.validateDataStructure(data)) {
      throw new Error('無効なインポートファイル形式です')
    }
    
    // バージョン互換性チェック（versionが無い古いファイルは1.0.0として扱う）
    const version = data.version || '1.0.0'
    if (!VersionManager.isCompatible(version)) {
      throw new Error(`バージョン ${version} のファイルはサポートされていません`)
    }
    
    // マイグレーションが必要な場合は実行
    if (VersionManager.needsMigration(version)) {
      const migrationResult = VersionManager.migrate(data, version)
      if (!migrationResult.success) {
        throw new Error(migrationResult.message)
      }
      return migrationResult.migratedData as BulkExportData
    }
    
    // 統計情報が無い場合は追加
    if (!data.statistics) {
      const stats: Record<string, number> = {}
      let total = 0
      
      if (data.data) {
        for (const [key, value] of Object.entries(data.data)) {
          if (Array.isArray(value)) {
            stats[key] = value.length
            total += value.length
          }
        }
      }
      
      data.statistics = {
        totalItems: total,
        byType: stats
      }
    }
    
    return data as BulkExportData
  }
  
  // 統計情報の取得
  getStatistics(stores: {
    names?: SavedName[]
    plots?: SavedPlot[]
    titles?: SavedTitle[]
    characters?: SavedCharacter[]
    relationships?: SavedRelationship[]
    wikipedias?: SavedWikipedia[]
    imagePrompts?: SavedImagePrompt[]
    enemies?: SavedEnemy[]
    items?: SavedItem[]
    worlds?: SavedWorldBuilding[]
    skills?: SavedSkill[]
    communities?: SavedCommunity[]
    dungeons?: SavedDungeon[]
    societies?: SavedSociety[]
    scenes?: SavedScene[]
    documents?: SavedDocument[]
  }): DataStatistics {
    return {
      names: stores.names?.length || 0,
      plots: stores.plots?.length || 0,
      titles: stores.titles?.length || 0,
      characters: stores.characters?.length || 0,
      relationships: stores.relationships?.length || 0,
      wikipedias: stores.wikipedias?.length || 0,
      imagePrompts: stores.imagePrompts?.length || 0,
      enemies: stores.enemies?.length || 0,
      items: stores.items?.length || 0,
      worlds: stores.worlds?.length || 0,
      skills: stores.skills?.length || 0,
      communities: stores.communities?.length || 0,
      dungeons: stores.dungeons?.length || 0,
      societies: stores.societies?.length || 0,
      scenes: stores.scenes?.length || 0,
      documents: stores.documents?.length || 0,
      total: 
        (stores.names?.length || 0) +
        (stores.plots?.length || 0) +
        (stores.titles?.length || 0) +
        (stores.characters?.length || 0) +
        (stores.relationships?.length || 0) +
        (stores.wikipedias?.length || 0) +
        (stores.imagePrompts?.length || 0) +
        (stores.enemies?.length || 0) +
        (stores.items?.length || 0) +
        (stores.worlds?.length || 0) +
        (stores.skills?.length || 0) +
        (stores.communities?.length || 0) +
        (stores.dungeons?.length || 0) +
        (stores.societies?.length || 0) +
        (stores.scenes?.length || 0) +
        (stores.documents?.length || 0)
    }
  }
  
  // 各データタイプのテキストフォーマット（詳細版）
  private formatNamesAsText(names: SavedName[]): string {
    let content = `【保存された名前】（${names.length}件）\n`
    content += '=' .repeat(50) + '\n\n'
    
    // 名前タイプ別にグループ化
    const grouped = names.reduce((acc, name) => {
      if (!acc[name.nameType]) {
        acc[name.nameType] = []
      }
      acc[name.nameType].push(name)
      return acc
    }, {} as Record<string, SavedName[]>)
    
    const typeLabels: Record<string, string> = {
      person_full: '人名（フルネーム）',
      person_family: '人名（名字）',
      person_given: '人名（名前）',
      weapon: '武器名',
      skill: 'スキル名',
      place: '地名',
      organization: '組織名',
      item: 'アイテム名',
      magic: '魔法名',
      title: '称号',
      enemy: 'エネミー名',
    }
    
    for (const [type, typeNames] of Object.entries(grouped)) {
      content += `\n【${typeLabels[type] || type}】 (${typeNames.length}件)\n`
      content += '-' .repeat(40) + '\n'
      typeNames.forEach((name) => {
        content += `${name.name}`
        if (name.reading) content += ` (${name.reading})`
        content += '\n'
        if (name.meaning) content += `  意味: ${name.meaning}\n`
        content += `  スタイル: ${name.style} | 珍しさ: ${name.rarity}/10`
        if (name.subcategory) content += ` | カテゴリ: ${name.subcategory}`
        if (name.memo) content += `\n  メモ: ${name.memo}`
        if (name.tags && name.tags.length > 0) content += `\n  タグ: ${name.tags.join(', ')}`
        content += '\n\n'
      })
    }
    
    return content
  }
  
  private formatPlotsAsText(plots: SavedPlot[]): string {
    let content = `【保存されたプロット】（${plots.length}件）\n`
    content += '=' .repeat(50) + '\n\n'
    
    plots.forEach((plot, i) => {
      const { result, input } = plot
      const date = new Date(plot.createdAt).toLocaleString('ja-JP')
      const storyLength = input.storyLength === 'short' ? '短編' : 
                          input.storyLength === 'long' ? '長編' : '中編'
      
      content += `\n【${i + 1}】 ${plot.title}\n`
      content += '━' .repeat(40) + '\n'
      content += `生成日時: ${date}\n\n`
      
      content += `【生成パラメータ】\n`
      content += `ジャンル: ${input.genreCustom || input.genre}\n`
      content += `舞台: ${input.stageCustom || input.stage}\n`
      content += `物語の長さ: ${storyLength}\n`
      if (result.keywords) content += `キーワード: ${result.keywords.join('、')}\n`
      content += `奇抜さレベル: ${input.creativityLevel}/10\n\n`
      
      content += `【前提】\n${result.premise}\n\n`
      content += `【あらすじ】\n${result.synopsis}\n\n`
      
      if (result.structure && result.structure.length > 0) {
        content += `【章構成】\n`
        result.structure.forEach((chapter) => {
          content += `第${chapter.number}章: ${chapter.title}\n`
          content += `${chapter.summary}\n\n`
        })
      }
      
      if (result.characters && result.characters.length > 0) {
        content += `【主要登場人物】\n`
        result.characters.forEach((char) => {
          content += `${char.name}: ${char.role}\n`
          if (char.description) content += `  ${char.description}\n`
        })
        content += '\n'
      }
      
      if (result.themes && result.themes.length > 0) {
        content += `【テーマ】\n${result.themes.join('、')}\n\n`
      }
      
      if (plot.memo) content += `【メモ】\n${plot.memo}\n\n`
      if (plot.tags && plot.tags.length > 0) content += `【タグ】\n${plot.tags.join('、')}\n\n`
      
      content += '=' .repeat(50) + '\n\n'
    })
    
    return content
  }
  
  private formatTitlesAsText(titles: SavedTitle[]): string {
    let content = `【保存されたタイトル】（${titles.length}件）\n`
    content += '=' .repeat(50) + '\n\n'
    
    titles.forEach((title, i) => {
      content += `\n【${i + 1}】 ${title.title}\n`
      content += '-' .repeat(40) + '\n\n'
      
      content += `【生成パラメータ】\n`
      content += `  スタイル: ${title.input.style}\n`
      content += `  タイプ: ${title.input.titleType === 'work' ? '作品全体' : 
                         title.input.titleType === 'chapter' ? '章' : '各話'}\n`
      content += `  創造性レベル: ${title.input.creativityLevel}/10\n`
      content += `  出力数: ${title.input.outputCount}\n\n`
      
      if (title.result.candidates && title.result.candidates.length > 0) {
        content += `【候補タイトル】\n`
        title.result.candidates.forEach((candidate, j) => {
          content += `${j + 1}. ${candidate.title}`
          if (candidate.reading) content += ` (${candidate.reading})`
          content += '\n'
          if (candidate.description) content += `   ${candidate.description}\n`
        })
        content += '\n'
      }
      
      if (title.memo) content += `【メモ】\n${title.memo}\n\n`
      if (title.tags && title.tags.length > 0) content += `【タグ】\n${title.tags.join(', ')}\n\n`
      
      content += '-' .repeat(40) + '\n'
    })
    
    return content
  }
  
  private formatCharactersAsText(characters: SavedCharacter[]): string {
    let content = `【保存されたキャラクター】（${characters.length}件）\n`
    content += '=' .repeat(50) + '\n\n'
    
    characters.forEach((character, index) => {
      const { result } = character
      content += `\n【${index + 1}】 ${result.name}\n`
      content += '━' .repeat(40) + '\n\n'
      
      // 基本情報
      content += `【基本情報】\n`
      content += `  名前: ${result.name}\n`
      content += `  年齢: ${result.age}\n`
      content += `  性別: ${result.gender}\n`
      content += `  役割: ${result.role}\n\n`
      
      // 概要
      content += `【概要】\n${result.summary}\n\n`
      
      // 性格
      if (result.personality) {
        content += `【性格】\n`
        content += `  特徴: ${result.personality.traits.join('、')}\n`
        content += `  強み: ${result.personality.strengths.join('、')}\n`
        content += `  弱み: ${result.personality.weaknesses.join('、')}\n`
        content += `  恐れ: ${result.personality.fears.join('、')}\n`
        content += `  欲求: ${result.personality.desires.join('、')}\n`
        content += `  価値観: ${result.personality.values.join('、')}\n\n`
      }
      
      // 背景
      if (result.background) {
        content += `【背景】\n`
        content += `  出身地: ${result.background.birthplace}\n`
        content += `  家族: ${result.background.family}\n`
        content += `  教育: ${result.background.education}\n`
        content += `  職業: ${result.background.occupation}\n`
        if (result.background.significantEvents && result.background.significantEvents.length > 0) {
          content += `  重要な出来事:\n`
          result.background.significantEvents.forEach(event => {
            content += `    - ${event}\n`
          })
        }
        content += '\n'
      }
      
      // 外見
      if (result.appearance) {
        content += `【外見】\n`
        content += `  身長: ${result.appearance.height}\n`
        content += `  体格: ${result.appearance.build}\n`
        content += `  髪色: ${result.appearance.hairColor}\n`
        content += `  髪型: ${result.appearance.hairStyle}\n`
        content += `  瞳の色: ${result.appearance.eyeColor}\n`
        content += `  全体的な印象: ${result.appearance.overallImpression}\n\n`
      }
      
      // 行動パターン
      if (result.behavior) {
        content += `【行動パターン】\n`
        content += `  一人称: ${result.behavior.firstPerson}\n`
        content += `  言葉遣い: ${result.behavior.speechPattern}\n`
        content += `  癖: ${result.behavior.mannerisms.join('、')}\n`
        content += `  習慣: ${result.behavior.habits.join('、')}\n\n`
      }
      
      if (character.memo) content += `【メモ】\n${character.memo}\n\n`
      if (character.tags && character.tags.length > 0) {
        content += `【タグ】\n${character.tags.join(', ')}\n\n`
      }
      
      content += '=' .repeat(50) + '\n\n'
    })
    
    return content
  }
  
  private formatRelationshipsAsText(relationships: SavedRelationship[]): string {
    let content = `【保存された関係性】（${relationships.length}件）\n`
    content += '=' .repeat(50) + '\n\n'
    
    relationships.forEach((relationship, index) => {
      const { result } = relationship
      content += `\n【${index + 1}】 ${relationship.title}\n`
      content += '━' .repeat(40) + '\n\n'
      
      // 概要
      content += `【概要】\n${result.summary}\n\n`
      
      // 登場人物
      content += `【登場人物】 (${result.characters.length}名)\n`
      result.characters.forEach(char => {
        content += `\n◆ ${char.name}`
        if (char.isMain) content += ' （主人公）'
        content += '\n'
        if (char.profile) content += `  プロフィール: ${char.profile}\n`
        if (char.personality) content += `  性格: ${char.personality}\n`
        if (char.background) content += `  背景: ${char.background}\n`
      })
      
      // 関係性リスト
      content += `\n【人物関係】\n`
      result.relationships.forEach(rel => {
        const sourceChar = result.characters.find(c => c.id === rel.source)
        const targetChar = result.characters.find(c => c.id === rel.target)
        
        if (sourceChar && targetChar) {
          content += `\n【${sourceChar.name} ⇔ ${targetChar.name}】\n`
          
          // ソースからターゲットへの関係
          content += `  ${sourceChar.name} → ${targetChar.name}:\n`
          content += `    関係: ${rel.fromSourceToTarget}\n`
          if (rel.sourceToTargetType) {
            content += `    タイプ: ${rel.sourceToTargetType}\n`
          } else if (rel.relationshipType) {
            content += `    タイプ: ${rel.relationshipType}\n`
          }
          
          // ターゲットからソースへの関係
          if (rel.fromTargetToSource) {
            content += `  ${targetChar.name} → ${sourceChar.name}:\n`
            content += `    関係: ${rel.fromTargetToSource}\n`
            if (rel.targetToSourceType) {
              content += `    タイプ: ${rel.targetToSourceType}\n`
            }
          }
          
          if (rel.strength) content += `  関係の強さ: ${rel.strength}/10\n`
          if (rel.description) content += `  説明: ${rel.description}\n`
        }
      })
      
      // 入力パラメータ
      if (relationship.input) {
        content += `\n【生成パラメータ】\n`
        content += `  キャラクター数: ${relationship.input.numberOfCharacters}\n`
        if (relationship.input.complexityLevel) {
          content += `  複雑さレベル: ${relationship.input.complexityLevel}/10\n`
        }
        if (relationship.input.mode) {
          content += `  モード: ${relationship.input.mode === 'detailed' ? '詳細' : 'イメージ'}\n`
        }
        if (relationship.input.additionalSettings) {
          content += `  追加設定: ${relationship.input.additionalSettings}\n`
        }
        content += '\n'
      }
      
      if (relationship.memo) content += `【メモ】\n${relationship.memo}\n\n`
      if (relationship.tags && relationship.tags.length > 0) {
        content += `【タグ】\n${relationship.tags.join(', ')}\n\n`
      }
      
      content += '=' .repeat(50) + '\n\n'
    })
    
    return content
  }
  
  private formatWikipediasAsText(wikipedias: SavedWikipedia[]): string {
    let content = `【保存された架空事典】（${wikipedias.length}件）\n`
    content += '=' .repeat(60) + '\n\n'
    
    wikipedias.forEach((wiki, index) => {
      const { result } = wiki
      content += `\n【${index + 1}】 ${result.title}\n`
      content += '━' .repeat(50) + '\n\n'
      
      // 基本情報
      content += `カテゴリ: ${result.category}\n`
      if (result.categories && result.categories.length > 0) {
        content += `分類: ${result.categories.join('、')}\n`
      }
      content += '\n'
      
      // インフォボックス
      if (result.infobox && Object.keys(result.infobox).length > 0) {
        content += `【基本情報】\n`
        Object.entries(result.infobox).forEach(([key, value]) => {
          content += `  ${key}: ${value}\n`
        })
        content += '\n'
      }
      
      // リードセクション
      content += `【概要】\n${result.leadSection}\n\n`
      
      // 各セクション
      if (result.sections && result.sections.length > 0) {
        result.sections.forEach(section => {
          content += `■ ${section.title}\n`
          content += `${section.content}\n\n`
          
          if (section.subsections && section.subsections.length > 0) {
            section.subsections.forEach(subsection => {
              content += `  ▪ ${subsection.title}\n`
              content += `  ${subsection.content}\n\n`
            })
          }
        })
      }
      
      // 関連項目
      if (result.seeAlso && result.seeAlso.length > 0) {
        content += `【関連項目】\n`
        result.seeAlso.forEach(item => {
          content += `  - ${item}\n`
        })
        content += '\n'
      }
      
      // 参考文献
      if (result.references && result.references.length > 0) {
        content += `【参考文献】\n`
        result.references.forEach((ref, i) => {
          content += `  [${i + 1}] ${ref}\n`
        })
        content += '\n'
      }
      
      if (wiki.memo) content += `【メモ】\n${wiki.memo}\n\n`
      if (wiki.tags && wiki.tags.length > 0) {
        content += `【タグ】\n${wiki.tags.join(', ')}\n\n`
      }
      
      content += '=' .repeat(60) + '\n\n'
    })
    
    return content
  }
  
  private formatImagePromptsAsText(prompts: SavedImagePrompt[]): string {
    let content = `【保存された画像プロンプト】（${prompts.length}件）\n`
    content += '=' .repeat(50) + '\n\n'
    
    prompts.forEach((prompt, index) => {
      content += `\n【${index + 1}】 ${prompt.title}\n`
      content += '-' .repeat(40) + '\n\n'
      
      // 基本情報
      content += `【基本設定】\n`
      content += `  タイプ: ${prompt.input.imageType === 'portrait' ? '立ち絵' : 
                         prompt.input.imageType === 'illustration' ? '一枚絵・シーン' : '背景'}\n`
      if (prompt.input.targetModel) {
        content += `  AIモデル: ${prompt.input.targetModel}\n`
      }
      if (prompt.input.composition) {
        content += `  構図: ${prompt.input.composition}\n`
      }
      if (prompt.input.backgroundType) {
        content += `  背景タイプ: ${prompt.input.backgroundType}\n`
      }
      if (prompt.input.backgroundColor) {
        content += `  背景色: ${prompt.input.backgroundColor}\n`
      }
      content += `  入力ソース: ${prompt.input.inputSource}\n`
      if (prompt.input.creativityLevel) {
        content += `  創造性レベル: ${prompt.input.creativityLevel}/10\n`
      }
      if (prompt.input.useProMode) {
        content += `  Proモード: 有効\n`
      }
      content += '\n'
      
      // 生成されたプロンプト
      content += `【生成されたプロンプト】\n`
      if (prompt.result.positivePrompt) {
        content += `ポジティブプロンプト:\n`
        content += `${prompt.result.positivePrompt}\n\n`
      }
      if (prompt.result.negativePrompt) {
        content += `ネガティブプロンプト:\n`
        content += `${prompt.result.negativePrompt}\n\n`
      }
      
      // モデル固有パラメータ
      if (prompt.result.modelSpecificParams) {
        const params = prompt.result.modelSpecificParams
        content += `【モデル固有パラメータ】\n`
        if (params.aspectRatio) content += `  アスペクト比: ${params.aspectRatio}\n`
        if (params.stylize) content += `  スタイライズ: ${params.stylize}\n`
        if (params.chaos) content += `  カオス: ${params.chaos}\n`
        if (params.version) content += `  バージョン: ${params.version}\n`
        if (params.cfgScale) content += `  CFGスケール: ${params.cfgScale}\n`
        if (params.steps) content += `  ステップ数: ${params.steps}\n`
        if (params.sampler) content += `  サンプラー: ${params.sampler}\n`
        if (params.clipSkip) content += `  CLIPスキップ: ${params.clipSkip}\n`
        content += '\n'
      }
      
      // 詳細入力情報
      if (prompt.input.manualInput) {
        const mi = prompt.input.manualInput
        content += `【詳細設定】\n`
        
        // 基本情報
        if (mi.characterName) content += `  キャラクター名: ${mi.characterName}\n`
        if (mi.gender) content += `  性別: ${mi.gender}\n`
        if (mi.age) content += `  年齢: ${mi.age}\n`
        if (mi.ageGroup) content += `  年齢層: ${mi.ageGroup}\n`
        if (mi.bodyType) content += `  体型: ${mi.bodyType}\n`
        if (mi.bodyTypeDetail) content += `  体型詳細: ${mi.bodyTypeDetail}\n`
        if (mi.expression) content += `  表情: ${mi.expression}\n`
        
        // 髪の詳細
        if (mi.hair) {
          content += `\n髪の設定:\n`
          if (mi.hair.front) content += `  前髪: ${mi.hair.front}\n`
          if (mi.hair.style) content += `  髪型: ${mi.hair.style}\n`
          if (mi.hair.texture) content += `  髪質: ${mi.hair.texture}\n`
          if (mi.hair.color) content += `  髪色: ${mi.hair.color}\n`
          if (mi.hair.colorEffect) content += `  色効果: ${mi.hair.colorEffect}\n`
        }
        
        // 目の詳細
        if (mi.eyes) {
          content += `\n目の設定:\n`
          if (mi.eyes.shape) content += `  形状: ${mi.eyes.shape}\n`
          if (mi.eyes.size) content += `  大きさ: ${mi.eyes.size}\n`
          if (mi.eyes.color) content += `  色: ${mi.eyes.color}\n`
        }
        
        // 顔の特徴
        if (mi.faceDetails?.features && mi.faceDetails.features.length > 0) {
          content += `\n顔の特徴: ${mi.faceDetails.features.join('、')}\n`
        }
        
        // 服装詳細
        if (mi.clothing) {
          content += `\n服装:\n`
          if (mi.clothing.tops) content += `  トップス: ${mi.clothing.tops}\n`
          if (mi.clothing.topsColor) content += `  トップス色: ${mi.clothing.topsColor}\n`
          if (mi.clothing.bottoms) content += `  ボトムス: ${mi.clothing.bottoms}\n`
          if (mi.clothing.bottomsColor) content += `  ボトムス色: ${mi.clothing.bottomsColor}\n`
          if (mi.clothing.onepiece) content += `  ワンピース: ${mi.clothing.onepiece}\n`
          if (mi.clothing.onepieceColor) content += `  ワンピース色: ${mi.clothing.onepieceColor}\n`
          if (mi.clothing.shoes) content += `  靴: ${mi.clothing.shoes}\n`
          if (mi.clothing.shoesColor) content += `  靴色: ${mi.clothing.shoesColor}\n`
          if (mi.clothing.accessories && mi.clothing.accessories.length > 0) {
            content += `  アクセサリー: ${mi.clothing.accessories.join('、')}\n`
          }
        }
        
        // ポーズ
        if (mi.pose) {
          content += `\nポーズ:\n`
          if (mi.pose.standing) content += `  立ちポーズ: ${mi.pose.standing}\n`
          if (mi.pose.gesture) content += `  ジェスチャー: ${mi.pose.gesture}\n`
          if (mi.pose.viewAngle) content += `  視点: ${mi.pose.viewAngle}\n`
        }
        
        // 世界観
        if (mi.worldSetting) {
          content += `\n世界観:\n`
          if (mi.worldSetting.type) content += `  ジャンル: ${mi.worldSetting.type}\n`
          if (mi.worldSetting.era) content += `  時代: ${mi.worldSetting.era}\n`
          if (mi.worldSetting.location) content += `  場所: ${mi.worldSetting.location}\n`
          if (mi.worldSetting.atmosphere) content += `  雰囲気: ${mi.worldSetting.atmosphere}\n`
        }
        
        // 画風
        if (mi.artStyle) {
          content += `\n画風:\n`
          if (mi.artStyle.period) content += `  年代: ${mi.artStyle.period}\n`
          if (mi.artStyle.technique) content += `  タッチ: ${mi.artStyle.technique}\n`
          if (mi.artStyle.mood) content += `  雰囲気: ${mi.artStyle.mood}\n`
          if (mi.artStyle.detailLevel) content += `  詳細度: ${mi.artStyle.detailLevel}\n`
          if (mi.artStyle.customStyle) content += `  カスタム: ${mi.artStyle.customStyle}\n`
        }
        
        // 追加情報
        if (mi.additionalInfo) {
          content += `\n追加情報:\n`
          if (mi.additionalInfo.text) content += `  ${mi.additionalInfo.text}\n`
        }
        
        // 自由記述
        if (mi.freeText) {
          content += `\n自由記述:\n${mi.freeText}\n`
        }
        
        content += '\n'
      }
      
      // 追加タグ
      if (prompt.input.additionalTags && prompt.input.additionalTags.length > 0) {
        content += `追加タグ: ${prompt.input.additionalTags.join('、')}\n`
      }
      if (prompt.input.negativeTags && prompt.input.negativeTags.length > 0) {
        content += `ネガティブタグ: ${prompt.input.negativeTags.join('、')}\n`
      }
      
      // メモとタグ
      if (prompt.memo) content += `\n【メモ】\n${prompt.memo}\n\n`
      if (prompt.tags && prompt.tags.length > 0) {
        content += `【タグ】\n${prompt.tags.join(', ')}\n\n`
      }
      
      content += '=' .repeat(50) + '\n\n'
    })
    
    return content
  }
  
  private formatEnemiesAsText(enemies: SavedEnemy[]): string {
    let content = `【保存されたエネミー・怪異】（${enemies.length}件）\n`
    content += '=' .repeat(50) + '\n\n'
    
    enemies.forEach((enemy, index) => {
      const { basicInfo, appearance, abilities, behavior, lore, encounter } = enemy.result
      
      content += `\n【${index + 1}】 ${enemy.title}\n`
      content += '━' .repeat(40) + '\n\n'
      
      // 基本情報
      content += `【基本情報】\n`
      content += `  名前: ${basicInfo.name}\n`
      content += `  タイプ: ${basicInfo.type}\n`
      content += `  カテゴリ: ${basicInfo.category}\n`
      content += `  脅威度: ${basicInfo.threat}\n`
      // サイズは appearance に移動
      content += `  生息地: ${basicInfo.habitat}\n`
      content += `  概要: ${basicInfo.summary}\n\n`
      
      // 外見
      content += `【外見】\n`
      content += `${appearance.overallDescription}\n`
      content += `  サイズ: ${appearance.size}\n`
      if (appearance.distinctiveFeatures && appearance.distinctiveFeatures.length > 0) {
        content += `\n特徴:\n`
        appearance.distinctiveFeatures.forEach(f => content += `  ・${f}\n`)
      }
      content += '\n'
      
      // 能力
      content += `【能力】\n`
      if (abilities.combatCapabilities?.attackPatterns && abilities.combatCapabilities.attackPatterns.length > 0) {
        content += `攻撃パターン:\n`
        abilities.combatCapabilities.attackPatterns.forEach(a => {
          content += `  ・${a.name}: ${a.damage} (${a.description})\n`
        })
      }
      if (abilities.combatCapabilities?.specialAbilities && abilities.combatCapabilities.specialAbilities.length > 0) {
        content += `\n特殊能力:\n`
        abilities.combatCapabilities.specialAbilities.forEach(s => {
          content += `  ・${s.name}: ${s.effect}\n`
          if (s.description) content += `    ${s.description}\n`
        })
      }
      if (abilities.weaknesses && abilities.weaknesses.length > 0) {
        content += `\n弱点:\n`
        abilities.weaknesses.forEach(w => content += `  ・${w.type}: ${w.description}\n`)
      }
      content += '\n'
      
      // 行動パターン
      content += `【行動パターン】\n`
      content += `${behavior.aiPattern}\n`
      content += `知能: ${behavior.intelligenceDescription}\n\n`
      
      // 伝承
      content += `【伝承】\n`
      if (lore.originStory) {
        content += `起源:\n${lore.originStory.narrative}\n\n`
      }
      if (lore.legends && lore.legends.length > 0) {
        content += `伝説:\n`
        lore.legends.forEach(l => {
          content += `『${l.title}』\n${l.story}\n\n`
        })
      }
      
      // 遭遇情報
      content += `【遭遇情報】\n`
      if (encounter.conditions) {
        content += `出現場所: ${encounter.conditions.location.join('、')}\n`
        content += `出現時間: ${encounter.conditions.time.join('、')}\n`
      }
      if (encounter.taboos && encounter.taboos.length > 0) {
        content += `禁忌:\n`
        encounter.taboos.forEach(t => content += `  ・${t.action}: ${t.consequence}\n`)
      }
      content += '\n'
      
      if (enemy.memo) content += `【メモ】\n${enemy.memo}\n\n`
      if (enemy.tags && enemy.tags.length > 0) {
        content += `【タグ】\n${enemy.tags.join(', ')}\n\n`
      }
      
      content += '=' .repeat(50) + '\n\n'
    })
    
    return content
  }
  
  private formatItemsAsText(items: SavedItem[]): string {
    let content = `【保存されたアイテム】（${items.length}件）\n`
    content += '=' .repeat(50) + '\n\n'
    
    items.forEach((item, index) => {
      const { basicInfo, appearance, effects, acquisition, lore } = item.result
      
      content += `\n【${index + 1}】 ${item.title || basicInfo.name}\n`
      content += '━' .repeat(40) + '\n\n'
      
      // 基本情報
      content += `【基本情報】\n`
      content += `  名前: ${basicInfo.name}\n`
      content += `  タイプ: ${basicInfo.type}\n`
      content += `  カテゴリ: ${basicInfo.category}\n`
      content += `  レア度: ${basicInfo.rarity}\n`
      content += `  概要: ${basicInfo.summary}\n`
      content += `  説明: ${basicInfo.description}\n\n`
      
      // 外見
      content += `【外見】\n`
      content += `${appearance.visualDescription}\n`
      content += `  素材: ${appearance.material}\n`
      content += `  サイズ: ${appearance.size}\n`
      content += `  重量: ${appearance.weight}\n`
      if (appearance.color) {
        content += `  色: ${appearance.color}\n`
      }
      if (appearance.distinctiveFeatures && appearance.distinctiveFeatures.length > 0) {
        content += `\n特徴:\n`
        appearance.distinctiveFeatures.forEach(f => content += `  ・${f}\n`)
      }
      content += '\n'
      
      // 効果
      content += `【効果】\n`
      content += `主要効果: ${effects.primaryEffect.description}\n`
      content += `  強さ: ${effects.primaryEffect.magnitude}\n`
      if (effects.primaryEffect.duration) {
        content += `  持続時間: ${effects.primaryEffect.duration}\n`
      }
      if (effects.secondaryEffects && effects.secondaryEffects.length > 0) {
        content += `\n副次効果:\n`
        effects.secondaryEffects.forEach(e => {
          content += `  ・${e.type}: ${e.description}\n`
        })
      }
      if (effects.sideEffects && effects.sideEffects.length > 0) {
        content += `\n副作用:\n`
        effects.sideEffects.forEach(s => content += `  ・${s}\n`)
      }
      // specialPropertiesは新しい型定義に存在しないため削除
      content += '\n'
      
      // 入手情報
      content += `【入手情報】\n`
      content += `  推定価値: ${acquisition.estimatedValue}`
      if (acquisition.currency) {
        content += ` ${acquisition.currency}`
      }
      content += '\n'
      content += `  レア度: ${acquisition.rarity}\n`
      if (acquisition.demandLevel) {
        content += `  需要: ${acquisition.demandLevel}\n`
      }
      if (acquisition.availability) {
        content += `  入手可能性: ${acquisition.availability}\n`
      }
      if (acquisition.valueFluctuation) {
        content += `  価値変動: ${acquisition.valueFluctuation}\n`
      }
      content += `\n入手方法:\n`
      acquisition.obtainMethod.forEach(m => content += `  ・${m}\n`)
      if (acquisition.locations && acquisition.locations.length > 0) {
        content += `\n入手場所:\n`
        acquisition.locations.forEach(l => content += `  ・${l}\n`)
      }
      if (acquisition.dropRate) {
        content += `\nドロップ率: ${acquisition.dropRate}\n`
      }
      if (acquisition.quest) {
        content += `\nクエスト報酬:\n`
        content += `  クエスト名: ${acquisition.quest.name}\n`
        content += `  依頼主: ${acquisition.quest.giver}\n`
      }
      content += '\n'
      
      // 伝承
      content += `【伝承】\n`
      // 新旧両方の形式に対応
      if ((lore as any).originStory) {
        const story = (lore as any).originStory
        if (story.narrative) {
          content += `${story.narrative}\n`
        }
        if (story.era) content += `  時代: ${story.era}\n`
        if (story.location) content += `  場所: ${story.location}\n`
        if (story.creator) content += `  創造者: ${story.creator}\n`
        if (story.purpose) content += `  目的: ${story.purpose}\n`
        if (story.firstOwner) content += `  最初の所有者: ${story.firstOwner}\n`
        if (story.creationMethod) content += `  製作方法: ${story.creationMethod}\n`
      } else if ((lore as any).origin) {
        const origin = (lore as any).origin
        content += `起源: ${origin.purpose || ''}\n`
        if (origin.creator) content += `  創造者: ${origin.creator}\n`
      }
      
      if ((lore as any).history && (lore as any).history.length > 0) {
        content += `\n歴史:\n`
        ;(lore as any).history.forEach((h: any) => {
          content += `  ${h.period}: ${h.event}\n`
        })
      }
      
      if (lore.legends && lore.legends.length > 0) {
        content += `\n伝説:\n`
        lore.legends.forEach(l => {
          content += `『${l.title}』\n${l.story}\n\n`
        })
      }
      
      // culturalSignificanceは新しい型定義に存在しないため削除
      
      if (lore.myths && lore.myths.length > 0) {
        content += `\n神話:\n`
        lore.myths.forEach(m => content += `  ・${m}\n`)
      }
      
      if (lore.flavorText) {
        content += `\n「${lore.flavorText}」\n`
      }
      content += '\n'
      
      if (item.memo) content += `【メモ】\n${item.memo}\n\n`
      if (item.tags && item.tags.length > 0) {
        content += `【タグ】\n${item.tags.join(', ')}\n\n`
      }
      
      content += '=' .repeat(50) + '\n\n'
    })
    
    return content
  }
  
  private formatWorldsAsText(worlds: SavedWorldBuilding[]): string {
    let content = `【保存された世界観】（${worlds.length}件）\n`
    content += '=' .repeat(50) + '\n\n'
    
    worlds.forEach((world, index) => {
      const { result } = world
      
      content += `\n【${index + 1}】 ${world.title}\n`
      content += '━' .repeat(40) + '\n\n'
      
      // 基本情報
      content += `【基本情報】\n`
      content += `  世界名: ${result.name}\n`
      content += `  コンセプト: ${result.concept}\n`
      content += `  概要: ${result.summary}\n`
      content += `  ジャンル: ${result.genres.join('、')}\n`
      content += `  規模: ${result.worldScale}\n\n`
      
      // パラメータ
      content += `【パラメータ】\n`
      content += `  魔法レベル: ${result.parameters.magicLevel}/10\n`
      content += `  科学技術: ${result.parameters.scienceLevel}/10\n`
      content += `  文明: ${result.parameters.civilizationLevel}/10\n`
      content += `  危険度: ${result.parameters.dangerLevel}/10\n`
      content += `  神秘性: ${result.parameters.mysteryLevel}/10\n`
      content += `  複雑さ: ${result.parameters.complexityLevel}/10\n\n`
      
      // 独自要素
      if (result.uniqueFeatures) {
        content += `【独自要素】\n`
        content += `  シグネチャー: ${result.uniqueFeatures.signature}\n`
        if (result.uniqueFeatures.laws && result.uniqueFeatures.laws.length > 0) {
          content += `  独自法則:\n`
          result.uniqueFeatures.laws.forEach(law => {
            content += `    ・${law}\n`
          })
        }
        content += '\n'
      }
      
      // 脅威と課題
      if (result.details?.threats?.hostileForces && result.details.threats.hostileForces.length > 0) {
        content += `【脅威と課題】\n`
        result.details.threats.hostileForces.forEach(threat => {
          content += `  ■ ${threat.name} (タイプ: ${threat.type})\n`
          content += `    ${threat.description}\n`
          if (threat.weaknesses && threat.weaknesses.length > 0) {
            content += `    弱点: ${threat.weaknesses.join('、')}\n`
          }
          content += '\n'
        })
      }
      
      // 文化と社会
      if (result.details?.cultures && result.details.cultures.length > 0) {
        content += `【文化と社会】\n`
        result.details.cultures.forEach(culture => {
          content += `  ■ ${culture.name}\n`
          content += `    ${culture.description}\n`
          if (culture.customs && culture.customs.length > 0) {
            content += `    慣習: ${culture.customs.join('、')}\n`
          }
          content += '\n'
        })
      }
      
      // 地理
      if (result.details?.geography?.notableLocations && result.details.geography.notableLocations.length > 0) {
        content += `【主要地域】\n`
        result.details.geography.notableLocations.forEach(location => {
          content += `  ■ ${location}\n`
          content += '\n'
        })
      }
      
      // 魔法体系
      if (result.details?.magicSystem && result.details.magicSystem.exists) {
        content += `【魔法体系】\n`
        // 名称フィールドは存在しない
        content += `  ${result.details.magicSystem.description}\n`
        if (result.details.magicSystem.rules && result.details.magicSystem.rules.length > 0) {
          content += `  規則: ${result.details.magicSystem.rules.join('、')}\n`
        }
        if (result.details.magicSystem.limitations && result.details.magicSystem.limitations.length > 0) {
          content += `  制限:\n`
          result.details.magicSystem.limitations.forEach(limit => {
            content += `    ・${limit}\n`
          })
        }
        content += '\n'
      }
      
      // 技術
      if (result.details?.technology) {
        content += `【技術】\n`
        content += `  技術レベル: ${result.details.technology.level}\n`
        if (result.details.technology.keyTechnologies && result.details.technology.keyTechnologies.length > 0) {
          content += `  主要技術:\n`
          result.details.technology.keyTechnologies.forEach(tech => {
            content += `    ・${tech}\n`
          })
        }
        if (result.details.technology.limitations && result.details.technology.limitations.length > 0) {
          content += `  制限:\n`
          result.details.technology.limitations.forEach(limit => {
            content += `    ・${limit}\n`
          })
        }
        content += '\n'
      }
      
      if (world.memo) content += `【メモ】\n${world.memo}\n\n`
      if (world.tags && world.tags.length > 0) {
        content += `【タグ】\n${world.tags.join(', ')}\n\n`
      }
      
      content += '=' .repeat(50) + '\n\n'
    })
    
    return content
  }
  
  private formatSkillsAsText(skills: SavedSkill[]): string {
    let content = `【保存されたスキル】（${skills.length}件）\n`
    content += '=' .repeat(50) + '\n\n'
    
    skills.forEach((skill, index) => {
      const result = skill.result
      
      content += `\n【${index + 1}】 ${result.basicInfo.name}\n`
      content += '━' .repeat(40) + '\n\n'
      
      // 基本情報
      content += `【基本情報】\n`
      content += `  名前: ${result.basicInfo.name}\n`
      content += `  タイプ: ${result.basicInfo.type}\n`
      content += `  カテゴリ: ${result.basicInfo.category}\n`
      content += `  階層: ${result.basicInfo.tier}\n`
      content += `  レアリティ: ${result.basicInfo.rarity}\n`
      content += `  レベル: ${result.basicInfo.level}\n`
      content += `  説明: ${result.basicInfo.description}\n\n`
      
      // 効果
      content += `【効果】\n`
      content += `  主効果: ${result.effects.primaryEffect.description}\n`
      content += `  威力: ${result.effects.primaryEffect.potency}\n`
      if (result.effects.secondaryEffects && result.effects.secondaryEffects.length > 0) {
        content += `  副次効果:\n`
        result.effects.secondaryEffects.forEach(effect => {
          content += `    ・${effect.description} (確率: ${effect.chance}%)\n`
        })
      }
      content += '\n'
      
      // コストとタイミング
      content += `【コスト】\n`
      content += `  ${result.mechanics.cost.primary.type}: ${result.mechanics.cost.primary.amount}\n`
      if (result.mechanics.cost.secondary) {
        content += `  ${result.mechanics.cost.secondary.type}: ${result.mechanics.cost.secondary.amount}\n`
      }
      content += `  クールダウン: ${result.mechanics.timing.cooldown}\n`
      if (result.mechanics.timing.castTime) {
        content += `  キャスト時間: ${result.mechanics.timing.castTime}\n`
      }
      if (result.mechanics.timing.channelDuration) {
        content += `  持続時間: ${result.mechanics.timing.channelDuration}\n`
      }
      content += '\n'
      
      // 演出
      content += `【演出】\n`
      content += `  準備: ${result.performance.visual.preparationEffect}\n`
      content += `  実行: ${result.performance.visual.executionEffect}\n`
      content += `  着弾: ${result.performance.visual.impactEffect}\n\n`
      
      // 進化
      if (result.evolution) {
        content += `【進化】\n`
        if (result.evolution.masteryLevels && result.evolution.masteryLevels.length > 0) {
          content += `  熟練度レベル:\n`
          result.evolution.masteryLevels.forEach((level: any) => {
            content += `    Lv${level.level} ${level.name}: ${level.improvements.join(', ')}\n`
          })
        }
        if (result.evolution.evolutionPaths && result.evolution.evolutionPaths.length > 0) {
          content += `  進化パス:\n`
          result.evolution.evolutionPaths.forEach((path: any) => {
            content += `    ${path.name}: ${path.description}\n`
          })
        }
        content += '\n'
      }
      
      // 伝承
      content += `【伝承】\n`
      content += `  創始者: ${result.lore.origin.creator}\n`
      content += `  由来: ${result.lore.origin.circumstances}\n`
      if (result.lore.historicalSignificance?.famousUsers && result.lore.historicalSignificance.famousUsers.length > 0) {
        content += `  著名な使用者:\n`
        result.lore.historicalSignificance.famousUsers.forEach((user: any) => {
          content += `    ・${user.name} - ${user.achievement}\n`
        })
      }
      content += '\n'
      
      if (skill.tags && skill.tags.length > 0) {
        content += `【タグ】\n${skill.tags.join(', ')}\n\n`
      }
      
      content += '=' .repeat(50) + '\n\n'
    })
    
    return content
  }
  
  private formatCommunitiesAsText(communities: SavedCommunity[]): string {
    let content = `【保存されたコミュニティ】（${communities.length}件）\n`
    content += '=' .repeat(50) + '\n\n'
    
    communities.forEach((community, index) => {
      const result = community.result
      
      content += `\n【${index + 1}】 ${result.basicInfo.name}\n`
      content += '━' .repeat(40) + '\n\n'
      
      // 基本情報
      content += `【基本情報】\n`
      content += `  名前: ${result.basicInfo.name}\n`
      content += `  タイプ: ${result.basicInfo.type}\n`
      content += `  人口: ${result.basicInfo.population}\n`
      content += `  規模: ${result.basicInfo.scale}\n`
      content += `  雰囲気: ${result.basicInfo.atmosphere}\n\n`
      
      if (community.tags && community.tags.length > 0) {
        content += `【タグ】\n${community.tags.join(', ')}\n\n`
      }
      
      content += '=' .repeat(50) + '\n\n'
    })
    
    return content
  }
  
  private formatDungeonsAsText(dungeons: SavedDungeon[]): string {
    let content = `【保存されたダンジョン】（${dungeons.length}件）\n`
    content += '=' .repeat(50) + '\n\n'
    
    dungeons.forEach((dungeon, index) => {
      const result = dungeon.result
      
      content += `\n【${index + 1}】 ${result.basicInfo.name}\n`
      content += '━' .repeat(40) + '\n\n'
      
      // 基本情報
      content += `【基本情報】\n`
      content += `  名前: ${result.basicInfo.name}\n`
      content += `  タイプ: ${result.basicInfo.type}\n`
      content += `  難易度: ${result.basicInfo.difficulty}\n`
      content += `  説明: ${result.basicInfo.description}\n\n`
      
      // 構造
      if (result.structure) {
        content += `【構造】\n`
        content += `  フロア数: ${result.structure.floors?.length || 0}\n`
        content += `  総部屋数: ${result.structure.totalRooms || '不明'}\n`
        content += `  レイアウト: ${result.structure.layout?.type || '不明'}\n\n`
      }
      
      if (dungeon.tags && dungeon.tags.length > 0) {
        content += `【タグ】\n${dungeon.tags.join(', ')}\n\n`
      }
      
      content += '=' .repeat(50) + '\n\n'
    })
    
    return content
  }
  
  private formatSocietiesAsText(societies: SavedSociety[]): string {
    let content = `【保存された組織】（${societies.length}件）\n`
    content += '=' .repeat(50) + '\n\n'
    
    societies.forEach((society, index) => {
      const result = society.result
      
      content += `\n【${index + 1}】 ${result.basicInfo.name}\n`
      content += '━' .repeat(40) + '\n\n'
      
      // 基本情報
      content += `【基本情報】\n`
      content += `  名前: ${result.basicInfo.name}\n`
      if (result.basicInfo.aliases && result.basicInfo.aliases.length > 0) {
        content += `  別名: ${result.basicInfo.aliases.join('、')}\n`
      }
      content += `  タイプ: ${result.basicInfo.type}\n`
      content += `  規模: ${result.basicInfo.scale}\n`
      if (result.basicInfo.foundingDate) {
        content += `  設立: ${result.basicInfo.foundingDate}\n`
      }
      if (result.basicInfo.motto) {
        content += `  モットー: ${result.basicInfo.motto}\n`
      }
      content += `  概要: ${result.basicInfo.summary}\n\n`
      
      // 組織構造
      if (result.structure) {
        content += `【組織構造】\n`
        content += `  メンバー数: ${result.structure.memberCount}\n`
        content += `  新規メンバー獲得: ${result.structure.recruitmentMethod}\n`
        content += `  意思決定: ${result.structure.decisionMaking}\n\n`
      }
      
      // メンバー
      if (result.members?.importantMembers && result.members.importantMembers.length > 0) {
        content += `【重要メンバー】\n`
        result.members.importantMembers.forEach((member, index) => {
          content += `  ${index + 1}. ${member.name} (${member.role})\n`
        })
        content += '\n'
      }
      
      // 特徴
      if (result.features) {
        if (result.features.activities) {
          content += `【活動内容】\n${result.features.activities}\n\n`
        }
        if (result.features.culture) {
          content += `【組織文化】\n${result.features.culture}\n\n`
        }
        if (result.features.relations) {
          content += `【他組織との関係】\n${result.features.relations}\n\n`
        }
      }
      
      if (society.tags && society.tags.length > 0) {
        content += `【タグ】\n${society.tags.join(', ')}\n\n`
      }
      
      content += '=' .repeat(50) + '\n\n'
    })
    
    return content
  }
  
  private formatScenesAsText(scenes: SavedScene[]): string {
    let content = `【保存されたシーン描写】（${scenes.length}件）\n`
    content += '=' .repeat(50) + '\n\n'
    
    scenes.forEach((scene, index) => {
      const title = scene.result?.title || (scene as any).title || `シーン ${scene.id.substring(0, 8)}`
      const wordCount = scene.result?.wordCount || scene.result?.fullText?.length || 0
      const summary = scene.result?.summary || ''
      
      content += `\n【${index + 1}】 ${title}\n`
      content += '━' .repeat(40) + '\n\n'
      
      // 基本情報
      content += `【基本情報】\n`
      content += `  タイトル: ${title}\n`
      content += `  文字数: ${wordCount}文字\n`
      if (scene.input?.mode) {
        const modeLabel = scene.input.mode === 'standard' ? '標準' :
                         scene.input.mode === 'continuation' ? '連続' :
                         scene.input.mode === 'outline' ? 'アウトライン' :
                         scene.input.mode === 'rewrite' ? 'リライト' : scene.input.mode
        content += `  モード: ${modeLabel}\n`
      }
      if (scene.input?.genre) {
        const genreLabel = scene.input.genre === 'fantasy' ? 'ファンタジー' :
                          scene.input.genre === 'sf' ? 'SF' :
                          scene.input.genre === 'modern' ? '現代' :
                          scene.input.genre === 'mystery' ? 'ミステリー' :
                          scene.input.genre === 'romance' ? 'ロマンス' :
                          scene.input.genre === 'horror' ? 'ホラー' :
                          scene.input.genre === 'historical' ? '歴史' :
                          scene.input.genre === 'comedy' ? 'コメディ' :
                          scene.input.genre === 'literary' ? '文学' : scene.input.genre
        content += `  ジャンル: ${genreLabel}\n`
      }
      if (scene.input?.perspective) {
        content += `  視点: ${scene.input.perspective}\n`
      }
      if (scene.input?.writingStyle) {
        content += `  文体: ${scene.input.writingStyle}\n`
      }
      content += `\n`
      
      // 要約
      if (summary) {
        content += `【要約】\n${summary}\n\n`
      }
      
      // 本文
      if (scene.result?.fullText) {
        content += `【本文】\n${scene.result.fullText}\n\n`
      }
      
      // メタデータ
      if (scene.result?.metadata) {
        const meta = scene.result.metadata
        if (meta.continuationInfo?.continuationType) {
          content += `【連続生成情報】\n`
          content += `  連続タイプ: ${meta.continuationInfo.continuationType}\n`
          if (meta.continuationInfo.previousSceneId) {
            content += `  前のシーンID: ${meta.continuationInfo.previousSceneId}\n`
          }
          content += `\n`
        }
      }
      
      // 入力パラメータ
      if (scene.input) {
        content += `【生成パラメータ】\n`
        if (scene.input.sceneSummary) {
          content += `  シーン概要: ${scene.input.sceneSummary}\n`
        }
        if (scene.input.sceneLocation) {
          content += `  場所: ${scene.input.sceneLocation}\n`
        }
        if (scene.input.timeOfDay) {
          content += `  時間帯: ${scene.input.timeOfDay}\n`
        }
        if (scene.input.atmosphere) {
          content += `  雰囲気: ${scene.input.atmosphere}\n`
        }
        if (scene.input.targetLength) {
          content += `  目標文字数: ${scene.input.targetLength}\n`
        }
        if (scene.input.creativityLevel !== undefined) {
          content += `  創造性レベル: ${scene.input.creativityLevel}/10\n`
        }
        if (scene.input.avoidElements && scene.input.avoidElements.length > 0) {
          content += `  避ける要素: ${scene.input.avoidElements.join('、')}\n`
        }
        content += `\n`
      }
      
      // 生成日時
      if (scene.timestamp) {
        content += `【生成日時】\n`
        content += `  ${new Date(scene.timestamp).toLocaleString('ja-JP')}\n\n`
      }
      
      content += '=' .repeat(50) + '\n\n'
    })
    
    return content
  }
  
  private formatDocumentsAsText(documents: SavedDocument[]): string {
    let content = `【保存された文献】（${documents.length}件）\n`
    content += '=' .repeat(50) + '\n\n'
    
    documents.forEach((document, index) => {
      const { result } = document
      
      content += `\n【${index + 1}】 ${document.title}\n`
      content += '━' .repeat(40) + '\n\n'
      
      // 基本情報
      content += `【基本情報】\n`
      content += `  文献タイプ: ${result.type}\n`
      content += `  作成日: ${new Date(document.createdAt).toLocaleString('ja-JP')}\n`
      if (document.updatedAt) {
        content += `  更新日: ${new Date(document.updatedAt).toLocaleString('ja-JP')}\n`
      }
      if (document.worldId) {
        content += `  世界観連携: あり\n`
      }
      if (document.characterIds && document.characterIds.length > 0) {
        content += `  キャラクター連携: ${document.characterIds.length}件\n`
      }
      content += '\n'
      
      // 文献内容（タイプによって異なる形式）
      content += `【文献内容】\n`
      
      // タイプごとの詳細表示
      if (result.type === 'history') {
        const history = result as any
        if (history.title) content += `タイトル: ${history.title}\n`
        if (history.period) content += `期間: ${history.period}\n`
        if (history.events && history.events.length > 0) {
          content += `\n出来事:\n`
          history.events.forEach((event: any) => {
            content += `  ${event.date}: ${event.title}\n`
            if (event.description) content += `    ${event.description}\n`
          })
        }
      } else if (result.type === 'government') {
        const gov = result as any
        if (gov.title) content += `タイトル: ${gov.title}\n`
        if (gov.organization) content += `組織: ${gov.organization}\n`
        if (gov.date) content += `日付: ${gov.date}\n`
        if (gov.content) content += `\n内容:\n${gov.content}\n`
      } else if (result.type === 'newspaper') {
        const news = result as any
        if (news.headline) content += `見出し: ${news.headline}\n`
        if (news.date) content += `日付: ${news.date}\n`
        if (news.author) content += `記者: ${news.author}\n`
        if (news.content) content += `\n記事内容:\n${news.content}\n`
      } else if (result.type === 'sns_twitter') {
        const tweet = result as any
        if (tweet.username) content += `ユーザー: @${tweet.username}\n`
        if (tweet.date) content += `日付: ${tweet.date}\n`
        if (tweet.content) content += `\n投稿:\n${tweet.content}\n`
        if (tweet.likes) content += `いいね: ${tweet.likes}\n`
        if (tweet.retweets) content += `リツイート: ${tweet.retweets}\n`
      } else if (result.type === 'diary') {
        const diary = result as any
        if (diary.date) content += `日付: ${diary.date}\n`
        if (diary.author) content += `著者: ${diary.author}\n`
        if (diary.location) content += `場所: ${diary.location}\n`
        if (diary.content) content += `\n内容:\n${diary.content}\n`
      } else if (result.type === 'report') {
        const report = result as any
        if (report.title) content += `タイトル: ${report.title}\n`
        if (report.date) content += `日付: ${report.date}\n`
        if (report.author) content += `報告者: ${report.author}\n`
        if (report.content) content += `\n内容:\n${report.content}\n`
      } else if (result.type === 'blog') {
        const blog = result as any
        if (blog.title) content += `タイトル: ${blog.title}\n`
        if (blog.date) content += `投稿日: ${blog.date}\n`
        if (blog.author) content += `著者: ${blog.author}\n`
        if (blog.content) content += `\n本文:\n${blog.content}\n`
      } else if (result.type === 'email') {
        const email = result as any
        if (email.date) content += `日付: ${email.date}\n`
        if (email.from) content += `差出人: ${email.from}\n`
        if (email.to) content += `宛先: ${email.to}\n`
        if (email.subject) content += `件名: ${email.subject}\n`
        if (email.content) content += `\n内容:\n${email.content}\n`
      } else if (result.type === 'memo') {
        const memo = result as any
        if (memo.date) content += `日付: ${memo.date}\n`
        if (memo.author) content += `作成者: ${memo.author}\n`
        if (memo.content) content += `\n内容:\n${memo.content}\n`
      } else if (result.type === 'letter') {
        const letter = result as any
        if (letter.date) content += `日付: ${letter.date}\n`
        if (letter.from) content += `差出人: ${letter.from}\n`
        if (letter.to) content += `宛先: ${letter.to}\n`
        if (letter.content) content += `\n内容:\n${letter.content}\n`
      } else if (result.type === 'medical') {
        const medical = result as any
        if (medical.patientId) content += `患者ID: ${medical.patientId}\n`
        if (medical.date) content += `日付: ${medical.date}\n`
        if (medical.physician) content += `医師: ${medical.physician}\n`
        if (medical.diagnosis) content += `診断: ${medical.diagnosis}\n`
        if (medical.treatment) content += `治療: ${medical.treatment}\n`
        if (medical.notes) content += `\n備考:\n${medical.notes}\n`
      } else if (result.type === 'police') {
        const police = result as any
        if (police.caseNumber) content += `事件番号: ${police.caseNumber}\n`
        if (police.date) content += `日付: ${police.date}\n`
        if (police.officer) content += `担当官: ${police.officer}\n`
        if (police.incident) content += `事件: ${police.incident}\n`
        if (police.report) content += `\n報告:\n${police.report}\n`
      } else if (result.type === 'academic') {
        const academic = result as any
        if (academic.title) content += `タイトル: ${academic.title}\n`
        if (academic.author) content += `著者: ${academic.author}\n`
        if (academic.field) content += `分野: ${academic.field}\n`
        if (academic.abstract) content += `\n要約:\n${academic.abstract}\n`
        if (academic.content) content += `\n本文:\n${academic.content}\n`
      } else if (result.type === 'magazine') {
        const magazine = result as any
        if (magazine.title) content += `タイトル: ${magazine.title}\n`
        if (magazine.issue) content += `号数: ${magazine.issue}\n`
        if (magazine.date) content += `発行日: ${magazine.date}\n`
        if (magazine.author) content += `著者: ${magazine.author}\n`
        if (magazine.content) content += `\n記事:\n${magazine.content}\n`
      } else if (result.type === 'line') {
        const line = result as any
        if (line.chatName) content += `チャット名: ${line.chatName}\n`
        if (line.messages && line.messages.length > 0) {
          content += `\nメッセージ:\n`
          line.messages.forEach((msg: any) => {
            content += `  [${msg.time}] ${msg.sender}: ${msg.content}\n`
          })
        }
      } else if (result.type === 'custom') {
        // カスタムタイプは汎用的に表示
        content += JSON.stringify(result, null, 2) + '\n'
      } else {
        // その他の場合も汎用的な表示
        content += JSON.stringify(result, null, 2) + '\n'
      }
      
      content += '\n'
      
      // メモとタグ
      if (document.memo) {
        content += `【メモ】\n${document.memo}\n\n`
      }
      if (document.tags && document.tags.length > 0) {
        content += `【タグ】\n${document.tags.join(', ')}\n\n`
      }
      
      // お気に入り
      if (document.favorite) {
        content += `【お気に入り】★\n\n`
      }
      
      // フォルダ
      if (document.folder) {
        content += `【フォルダ】${document.folder}\n\n`
      }
      
      content += '=' .repeat(50) + '\n\n'
    })
    
    return content
  }
}